import { useEffect, useMemo, useRef, useState } from 'react';
import {
  calculateFundingQuote,
  calculateMinimumFundingQuote,
  getFundingAssetKey,
} from '../services/fundingAssets';
import { decimalEq, decimalToString, parseDecimalInput } from '../utils/decimalValues';

const QUOTE_DEBOUNCE_MS = 250;
const EMPTY_CALCULATION = {
  key: '',
  data: null,
  error: null,
  isLoading: false,
};

function getPositiveAmountKey(value) {
  const amount = parseDecimalInput(value);

  return amount?.gt(0) ? decimalToString(amount) : '';
}

function getCalculationKey(fundingAsset, amount) {
  if (!fundingAsset?.currencyId || !fundingAsset?.outcomeCurrencyId) {
    return '';
  }

  const assetKey = getFundingAssetKey(fundingAsset);
  const amountKey = getPositiveAmountKey(amount);

  return assetKey && amountKey ? `${assetKey}:${amountKey}` : '';
}

export default function useFundingQuote({
  amount,
  fundingAsset,
  minimumBalanceAmount,
  enabled = true,
}) {
  const minimumRequestIdRef = useRef(0);
  const quoteRequestIdRef = useRef(0);
  const [minimumCalculation, setMinimumCalculation] = useState(EMPTY_CALCULATION);
  const [quoteCalculation, setQuoteCalculation] = useState(EMPTY_CALCULATION);
  const minimumKey = enabled
    ? getCalculationKey(fundingAsset, minimumBalanceAmount)
    : '';
  const quoteKey = enabled ? getCalculationKey(fundingAsset, amount) : '';

  useEffect(() => {
    const requestId = minimumRequestIdRef.current + 1;

    minimumRequestIdRef.current = requestId;

    if (!minimumKey) {
      setMinimumCalculation(EMPTY_CALCULATION);
      return undefined;
    }

    let isActive = true;

    setMinimumCalculation({
      key: minimumKey,
      data: null,
      error: null,
      isLoading: true,
    });

    calculateMinimumFundingQuote(minimumBalanceAmount, fundingAsset)
      .then((data) => {
        if (!isActive || minimumRequestIdRef.current !== requestId) return;

        setMinimumCalculation({
          key: minimumKey,
          data,
          error: null,
          isLoading: false,
        });
      })
      .catch((error) => {
        if (!isActive || minimumRequestIdRef.current !== requestId) return;

        setMinimumCalculation({
          key: minimumKey,
          data: null,
          error,
          isLoading: false,
        });
      });

    return () => {
      isActive = false;
    };
  }, [fundingAsset, minimumBalanceAmount, minimumKey]);

  const currentMinimumQuote = minimumCalculation.key === minimumKey
    ? minimumCalculation.data
    : null;
  const canReuseMinimumQuote = Boolean(
    quoteKey
    && currentMinimumQuote
    && decimalEq(amount, currentMinimumQuote.fundingAmount),
  );

  useEffect(() => {
    const requestId = quoteRequestIdRef.current + 1;

    quoteRequestIdRef.current = requestId;

    if (!quoteKey || canReuseMinimumQuote) {
      setQuoteCalculation(EMPTY_CALCULATION);
      return undefined;
    }

    let isActive = true;

    setQuoteCalculation({
      key: quoteKey,
      data: null,
      error: null,
      isLoading: true,
    });

    const timeoutId = window.setTimeout(() => {
      calculateFundingQuote(amount, fundingAsset)
        .then((data) => {
          if (!isActive || quoteRequestIdRef.current !== requestId) return;

          setQuoteCalculation({
            key: quoteKey,
            data,
            error: null,
            isLoading: false,
          });
        })
        .catch((error) => {
          if (!isActive || quoteRequestIdRef.current !== requestId) return;

          setQuoteCalculation({
            key: quoteKey,
            data: null,
            error,
            isLoading: false,
          });
        });
    }, QUOTE_DEBOUNCE_MS);

    return () => {
      isActive = false;
      window.clearTimeout(timeoutId);
    };
  }, [amount, canReuseMinimumQuote, fundingAsset, quoteKey]);

  const currentQuoteCalculation = quoteCalculation.key === quoteKey
    ? quoteCalculation
    : EMPTY_CALCULATION;
  const quote = canReuseMinimumQuote ? currentMinimumQuote : currentQuoteCalculation.data;
  const quoteError = canReuseMinimumQuote ? null : currentQuoteCalculation.error;
  const isQuoteLoading = Boolean(
    quoteKey
    && !quote
    && !quoteError,
  );

  return useMemo(() => ({
    quote,
    quoteError,
    isQuoteLoading,
    minimumQuote: currentMinimumQuote,
    minimumQuoteError: minimumCalculation.key === minimumKey
      ? minimumCalculation.error
      : null,
    isMinimumQuoteLoading: Boolean(
      minimumKey
      && minimumCalculation.key === minimumKey
      && minimumCalculation.isLoading,
    ),
  }), [
    currentMinimumQuote,
    isQuoteLoading,
    minimumCalculation.error,
    minimumCalculation.isLoading,
    minimumCalculation.key,
    minimumKey,
    quote,
    quoteError,
  ]);
}
